import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { Mail, MapPin, Phone, Download } from 'lucide-react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import logo from '../assets/logo.png'

gsap.registerPlugin(ScrollTrigger)

const sections = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about-us' },
  { label: 'Our Team', href: '/our-team' },
  { label: 'Accreditation', href: '/gallery' },
  { label: 'Products', href: '/all-products' },

  // BLOGS
  { label: 'Blogs', href: '/blogs' },

  {
    label: 'Trade Fair Participation',
    href: '/trade-fare-participation',
  },

  { label: 'Contact Us', href: '/contact' },

  {
    label: 'E-catalogue',
    href: '/e-catalogue',
    isDownload: true,
  },

  {
    label: 'Terms and Conditions',
    href: '/terms-and-condition',
  },

  {
    label: 'Privacy Policy',
    href: '/privacy-policy',
  },

  {
    label: 'Return and Exchange Policy',
    href: '/return',
  },
]

const MAROON_DEEP = 'var(--user-maroon, #1A0000)'
const GOLD = 'var(--user-gold, #D4AF37)'
const GOLD_SOFT = 'var(--user-gold-soft, #E9CD7A)'
const CREAM = 'var(--user-cream, #F3E9D8)'

export default function Footer() {
  const rootRef = useRef(null)

  // Handle E-catalogue download
  const handleDownload = (e) => {
    e.preventDefault()
    // Import the PDF file - make sure the path is correct
    import('../assets/catalogue.pdf')
      .then(module => {
        const pdfUrl = module.default
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a')
        link.href = pdfUrl
        link.download = 'SL-Gold-Kanthi-E-Catalogue.pdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch(error => {
        console.error('Error loading PDF:', error)
        // Fallback: try to open in new tab if download fails
        window.open('/e-catalogue', '_blank')
      })
  }

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

      if (reduceMotion) return

      gsap.set(
        '[data-anim="col"], [data-anim="hairline"], [data-anim="bottom-bar"]',
        { willChange: 'transform, opacity' }
      )

      const timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
      })

      timeline
        .fromTo(
          '[data-anim="hairline"]',
          { scaleX: 0, opacity: 0 },
          { scaleX: 1, opacity: 1, duration: 0.8, ease: 'power2.out' }
        )
        .from(
          '[data-anim="col"]',
          {
            y: 28,
            opacity: 0,
            duration: 0.6,
            stagger: 0.12,
          },
          '-=0.35'
        )
        .from(
          '[data-anim="bottom-bar"]',
          { y: 12, opacity: 0, duration: 0.45 },
          '-=0.2'
        )
    },
    { scope: rootRef }
  )

  return (
    <footer
      ref={rootRef}
      className="mt-auto"
      style={{ backgroundColor: MAROON_DEEP, color: CREAM }}
    >
      {/* Signature gold hairline, echoes the navbar */}
      <div
        data-anim="hairline"
        className="h-px w-full origin-center"
        style={{
          background: `linear-gradient(90deg, transparent, ${GOLD} 20%, ${GOLD_SOFT} 50%, ${GOLD} 80%, transparent)`,
        }}
      />

      <div className="container-x py-12 md:py-16">
        <div className="grid gap-12 md:grid-cols-[1.2fr_0.9fr_1fr]">
          {/* Brand */}
          <div data-anim="col">
            <Link to="/" className="inline-flex items-center gap-4">
              <span
                className="grid h-16 w-16 shrink-0 place-items-center rounded-full p-0.5"
                style={{ boxShadow: '0 0 0 1px rgba(212,175,55,0.5)' }}
              >
                <img
                  src={logo}
                  alt="SL Gold"
                  className="h-full w-full rounded-full object-contain"
                />
              </span>
              <span>
                <span
                  className="block text-xl font-semibold tracking-wide"
                  style={{ color: GOLD_SOFT }}
                >
                  SL Gold
                </span>
                <span className="block text-sm" style={{ color: CREAM }}>
                  Premium jewellery collections
                </span>
              </span>
            </Link>

            <p className="mt-5 max-w-sm text-sm leading-6 text-white/70">
              Crafted pieces, timeless designs, and trusted service — trading
              and exporting fine gold jewellery with a legacy of quality.
            </p>

            <div
              className="mt-6 h-px w-16"
              style={{ backgroundColor: 'rgba(212,175,55,0.35)' }}
            />
          </div>

          {/* Sections */}
          <div data-anim="col">
            <h2
              className="mb-4 text-sm font-semibold uppercase tracking-[0.18em]"
              style={{ color: GOLD_SOFT }}
            >
              Sections
            </h2>
            <nav className="flex flex-col gap-2.5">
              {sections.map((link) => {
                // Check if it's the E-catalogue link with download
                if (link.isDownload) {
                  return (
                    <button
                      key={link.label}
                      onClick={handleDownload}
                      className="group inline-flex w-fit items-center text-sm text-white/70 transition-colors duration-200 hover:text-white cursor-pointer"
                    >
                      <span
                        className="mr-0 h-px w-0 transition-all duration-200 group-hover:mr-2 group-hover:w-3"
                        style={{ backgroundColor: GOLD }}
                      />
                      <Download className="mr-1.5 h-3.5 w-3.5" style={{ color: GOLD }} />
                      {link.label}
                    </button>
                  )
                }
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="group inline-flex w-fit items-center text-sm text-white/70 transition-colors duration-200 hover:text-white"
                  >
                    <span
                      className="mr-0 h-px w-0 transition-all duration-200 group-hover:mr-2 group-hover:w-3"
                      style={{ backgroundColor: GOLD }}
                    />
                    {link.label}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Contact */}
          <div data-anim="col">
            <h2
              className="mb-4 text-sm font-semibold uppercase tracking-[0.18em]"
              style={{ color: GOLD_SOFT }}
            >
              Contact
            </h2>

            <div className="space-y-5 text-sm text-white/70">
              <div>
                <div
                  className="mb-2 flex items-start gap-3"
                >
                  <MapPin
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: GOLD }}
                  />
                  <div>
                    <p className="font-medium" style={{ color: CREAM }}>
                      Head Office
                    </p>
                    <p className="mt-1 leading-relaxed text-white/65">
                      Building No.1149, Shop No.5, 1st floor, Kucha Mahajani,
                      Chandani Chowk, Delhi, Delhi, India 110006
                    </p>
                  </div>
                </div>
                <a
                  href="tel:+918640051400"
                  className="ml-7 flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                  <span>+91-86400-51400</span>
                </a>
                <a
                  href="mailto:slgoldkanthi@info.com"
                  className="ml-7 mt-1 flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Mail className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                  <span>slgoldkanthi@info.com</span>
                </a>
              </div>

              <div
                className="pt-1"
                style={{ borderTop: '1px solid rgba(212,175,55,0.15)' }}
              >
                <p
                  className="mb-2 pt-4 font-medium"
                  style={{ color: CREAM }}
                >
                  Export Department
                </p>
                <a
                  href="tel:+918640052400"
                  className="flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                  <span>+91-86400-52400</span>
                </a>
                <a
                  href="mailto:slgoldindia@gmail.com"
                  className="mt-1 flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Mail className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                  <span>slgoldindia@gmail.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        data-anim="bottom-bar"
        style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="container-x flex flex-col gap-3 py-5 text-xs text-white/55 md:flex-row md:items-center md:justify-between">
          <p>SL Gold Copyright © 2026. All Right Reserved</p>
          <div className="flex gap-4">
            <Link
              to="/privacy-policy"
              className="transition-colors hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-and-condition"
              className="transition-colors hover:text-white"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}